import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import {
  Calendar,
  MapPin,
  Clock,
  Zap,
  Plus,
  LogOut,
  Settings,
  TrendingUp,
  Search,
  Filter,
  Eye,
  Map,
} from "lucide-react";
import axios from "axios";
import { API } from "../lib/api";
import { useCustomer } from "../contexts/CustomerContext";
import { toast } from "sonner";

export const CustomerDashboard = () => {
  const navigate = useNavigate();
  const { customer, token, logout } = useCustomer();
  const [bookings, setBookings] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");

  // Redirect if not authenticated
  useEffect(() => {
    if (!customer || !token) {
      navigate("/");
    }
  }, [customer, token, navigate]);

  // Fetch dashboard data
  useEffect(() => {
    if (customer && token) {
      fetchDashboard();
    }
  }, [customer, token]);

  const fetchDashboard = async () => {
    try {
      setLoading(true);
      console.log("📊 Fetching dashboard for:", customer?.email);
      const response = await axios.get(`${API}/customer/dashboard`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      console.log("✅ Dashboard response:", response.data);
      setBookings(response.data.bookings || []);
      setStats(response.data.stats || { total_bookings: 0, total_spent: 0, active_bookings: 0 });
    } catch (err) {
      console.error("❌ Failed to fetch dashboard:", err);
      console.error("   Status:", err.response?.status);
      console.error("   Detail:", err.response?.data?.detail);
      toast.error(err.response?.data?.detail || "Failed to load dashboard");
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate("/");
    toast.success("Logged out successfully");
  };

  const filteredBookings = bookings.filter((booking) => {
    const matchesFilter =
      filter === "all" || booking.status.toLowerCase() === filter.toLowerCase();
    const matchesSearch =
      booking.service_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      booking.booking_id.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesFilter && matchesSearch;
  });

  const getStatusColor = (status) => {
    const colors = {
      confirmed: "bg-blue-100 text-blue-700",
      assigned: "bg-purple-100 text-purple-700",
      on_the_way: "bg-orange-100 text-orange-700",
      completed: "bg-green-100 text-green-700",
      cancelled: "bg-red-100 text-red-700",
    };
    return colors[status?.toLowerCase()] || "bg-slate-100 text-slate-700";
  };

  const getStatusLabel = (status) => {
    const labels = {
      confirmed: "Confirmed",
      assigned: "Technician Assigned",
      on_the_way: "On The Way",
      completed: "Completed",
      cancelled: "Cancelled",
    };
    return labels[status?.toLowerCase()] || status;
  };

  if (!customer) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      {/* Header */}
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-3xl font-bold text-slate-900">
                Welcome back, {customer.name}!
              </h1>
              <p className="text-slate-500 mt-1">{customer.email || customer.phone}</p>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={() => navigate("/booking")}
                className="flex items-center gap-2 px-4 py-2.5 bg-[#FF6B2B] hover:bg-[#E65A20] text-white rounded-lg font-semibold transition"
              >
                <Plus className="w-4 h-4" /> New Booking
              </button>
              <button
                onClick={() => navigate("/dashboard/settings")}
                className="p-2 border border-slate-200 rounded-lg hover:bg-slate-50 transition"
              >
                <Settings className="w-5 h-5 text-slate-600" />
              </button>
              <button
                onClick={handleLogout}
                className="p-2 border border-slate-200 rounded-lg hover:bg-slate-50 transition"
              >
                <LogOut className="w-5 h-5 text-slate-600" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            <div className="bg-white rounded-lg border border-slate-200 p-6 hover:shadow-md transition">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-slate-500 text-sm font-medium">
                    Total Bookings
                  </p>
                  <p className="text-3xl font-bold text-slate-900 mt-2">
                    {stats.total_bookings}
                  </p>
                </div>
                <Zap className="w-12 h-12 text-[#FF6B2B]/20" />
              </div>
            </div>

            <div className="bg-white rounded-lg border border-slate-200 p-6 hover:shadow-md transition">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-slate-500 text-sm font-medium">
                    Active Bookings
                  </p>
                  <p className="text-3xl font-bold text-slate-900 mt-2">
                    {stats.active_bookings}
                  </p>
                </div>
                <TrendingUp className="w-12 h-12 text-blue-500/20" />
              </div>
            </div>

            <div className="bg-white rounded-lg border border-slate-200 p-6 hover:shadow-md transition">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-slate-500 text-sm font-medium">
                    Total Spent
                  </p>
                  <p className="text-3xl font-bold text-slate-900 mt-2">
                    ₹{stats.total_spent}
                  </p>
                </div>
                <TrendingUp className="w-12 h-12 text-green-500/20" />
              </div>
            </div>
          </div>
        )}

        {/* Bookings Section */}
        <div className="bg-white rounded-lg border border-slate-200">
          {/* Filters */}
          <div className="border-b border-slate-200 p-6">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-3 w-5 h-5 text-slate-400" />
                <input
                  type="text"
                  placeholder="Search bookings by service or ID..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#FF6B2B]"
                />
              </div>
              <div className="flex items-center gap-2">
                <Filter className="w-5 h-5 text-slate-400" />
                <select
                  value={filter}
                  onChange={(e) => setFilter(e.target.value)}
                  className="px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#FF6B2B]"
                >
                  <option value="all">All Bookings</option>
                  <option value="confirmed">Confirmed</option>
                  <option value="assigned">Assigned</option>
                  <option value="on_the_way">On The Way</option>
                  <option value="completed">Completed</option>
                </select>
              </div>
            </div>
          </div>

          {/* Bookings List */}
          <div>
            {loading ? (
              <div className="p-12 text-center">
                <div className="inline-block animate-spin">
                  <Zap className="w-8 h-8 text-[#FF6B2B]" />
                </div>
                <p className="text-slate-500 mt-2">Loading bookings...</p>
              </div>
            ) : filteredBookings.length === 0 ? (
              <div className="p-12 text-center">
                <p className="text-slate-500">
                  {bookings.length === 0
                    ? "No bookings yet. Create your first booking!"
                    : "No bookings match your search."}
                </p>
              </div>
            ) : (
              <div className="divide-y divide-slate-200">
                {filteredBookings.map((booking) => (
                  <div
                    key={booking.booking_id}
                    className="p-6 hover:bg-slate-50 transition"
                  >
                    <div className="flex justify-between items-start mb-4">
                      <div>
                        <h3 className="text-lg font-semibold text-slate-900">
                          {booking.service_name}
                        </h3>
                        <p className="text-sm text-slate-500">
                          ID: {booking.booking_id}
                        </p>
                      </div>
                      <span
                        className={`px-3 py-1 rounded-full text-sm font-semibold ${getStatusColor(
                          booking.status
                        )}`}
                      >
                        {getStatusLabel(booking.status)}
                      </span>
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm mb-4">
                      <div className="flex items-center gap-2 text-slate-600">
                        <Calendar className="w-4 h-4 text-slate-400" />
                        {booking.date}
                      </div>
                      <div className="flex items-center gap-2 text-slate-600">
                        <Clock className="w-4 h-4 text-slate-400" />
                        {booking.slot}
                      </div>
                      <div className="flex items-center gap-2 text-slate-600">
                        <MapPin className="w-4 h-4 text-slate-400" />
                        {booking.city}
                      </div>
                      <div className="font-semibold text-slate-900">
                        ₹{booking.price}
                      </div>
                    </div>

                    {booking.technician_name && (
                      <div className="mb-4 text-sm text-slate-600">
                        Technician: <span className="font-semibold">{booking.technician_name}</span>
                      </div>
                    )}

                    {/* Action Buttons */}
                    <div className="flex gap-3 pt-4 border-t border-slate-100">
                      <button
                        onClick={() =>
                          navigate(
                            `/dashboard/bookings/${booking.booking_id}`
                          )
                        }
                        className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-blue-600 hover:bg-blue-50 rounded-lg transition"
                      >
                        <Eye className="w-4 h-4" />
                        View Details
                      </button>
                      <Link
                        to={`/booking/track/${booking.booking_id}`}
                        className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-orange-600 hover:bg-orange-50 rounded-lg transition"
                      >
                        <Map className="w-4 h-4" />
                        Track Booking
                      </Link>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CustomerDashboard;
